/* eslint-disable react-hooks/exhaustive-deps */
import {
  Alert,
  Button,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import { useToggle } from '@tonic-one/react-hooks';
import CSpinner from '../../../components/CSpinner';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { Steps } from 'antd';
import {
  DataSynced,
  DataSyncFailed,
  DataSyncing,
  ExchangeInlineLearnMore,
  ProvisionOrgInfo,
  StepIcon,
  UserIcon,
} from './share';
import { Trans, useTranslation } from 'react-i18next';
import { deleteCookie, getProvisionCookie } from '@Utils/common';
import {
  EnabledPolicyTypeEnum,
  IdSyncStatus,
  MultiAppsAuthenticationInfoRes,
  ProvisionModalState,
  ProvisionStatusEnum,
  RejectProvisionReason,
  TokenStatus,
} from '@Types/provision';
import { ProvisionApi } from '@Apis';
import { reportPendo } from '@Utils/setupPendo';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import RejectProvisionModal from './rejectProvisionModal';
import { CAS_DOC_LINK, getCasOLHLink } from '@Utils/casDocLink';
import i18next from 'i18next';
import { App, Service } from '@Types/app';

//后台传来的是 做完的状态
enum ExchangeInlineStatus {
  NotProvision = 0,
  ProvisioningGrantStep1Done = 1,
  ProvisioningConfirmDone = 2,
  ProvisioningGrantStep2Done = 3, //这个状态就是ProvisioningSyncing了
  Provisioned = 4,
}

const SERVICE = Service.ExchangeInline;

const ExchangeInlineModal = ({
  orgId,
  onClose,
  isOpen,
  provisionStatus,
}: ProvisionModalState) => {
  const { t } = useTranslation('provision');
  const [errorMsg, setErrorMsg] = useState<ReactNode | undefined>(undefined);

  const getInitStatus = () => {
    if (provisionStatus.status === ProvisionStatusEnum.NotStarted) {
      return ExchangeInlineStatus.NotProvision;
    } else if (provisionStatus.status === ProvisionStatusEnum.Provisioned) {
      return ExchangeInlineStatus.Provisioned;
    } else {
      const { apps: appInfo } = provisionStatus?.authorization as {
        apps: {
          app: string;
          status: string;
        }[];
      };
      //第一个app是exchangeInlineMailFlowApp，第二个是exchangeInlineApp
      const [exchangeInlineMailFlowApp, exchangeInlineApp] = appInfo;
      if (provisionStatus.status === ProvisionStatusEnum.Provisioning) {
        //默认第一个app肯定成功了，只要确认第二个app是否已经provision
        //判断是 ProvisioningGrantStep1Done 还是 ProvisioningGrantStep2Done
        if (exchangeInlineApp.status === TokenStatus.NotStarted) {
          return ExchangeInlineStatus.ProvisioningGrantStep1Done;
        } else {
          //默认是TokenStatus.Created
          return ExchangeInlineStatus.ProvisioningGrantStep2Done;
        }
      } else {
        const statusArr = [
          TokenStatus.NotStarted,
          TokenStatus.Failure,
        ] as string[];
        //check which step has done fail
        if (statusArr.includes(exchangeInlineMailFlowApp.status)) {
          //first permission not success, set to not provision
          return ExchangeInlineStatus.NotProvision;
        } else if (statusArr.includes(exchangeInlineApp.status)) {
          //second permission not success, set to step 1 done
          return ExchangeInlineStatus.ProvisioningGrantStep1Done;
        } else {
          //sync data fail
          return ExchangeInlineStatus.ProvisioningGrantStep2Done;
        }
      }
    }
  };

  const [syncDataFailFlag, setSyncDataFailFlag] = useState(
    provisionStatus.status === ProvisionStatusEnum.Failure &&
      [IdSyncStatus.Failure, 'FAILURE'].includes(
        provisionStatus?.idSync?.status
      )
  );
  const [exchangeInlineStatus, setExchangeInlineStatus] =
    useState<ExchangeInlineStatus>(getInitStatus());

  //example:
  // [
  //   {
  //     app: 'exchangeInline',
  //     installAppUrl: null,
  //     authenticationUrl:
  //       'https://login.windows.net/common/oauth2/authorize?client_id=f362d789-5a05-4faa-9708-70933c56e3ab&response_type=code&prompt=consent&resource=https://graph.microsoft.com&state=azuread_mail_inline_graph_api_98829450685893498823c6ebec592aba&redirect_uri=https%3A%2F%2Fadmin-dev.tmcas.trendmicro.net%2Fprovision.html',
  //   },
  //   {
  //     app: 'exchangeInlineMailFlow',
  //     installAppUrl: null,
  //     authenticationUrl:
  //       'https://login.windows.net/common/oauth2/authorize?client_id=8af0038e-67da-438d-9424-ce0e1e5f6325&response_type=code&prompt=consent&resource=https://graph.microsoft.com&state=azuread_mail_inline_graph_api_98829450685893498823c6ebec592aba&redirect_uri=https%3A%2F%2Fadmin-dev.tmcas.trendmicro.net%2Fprovision.html',
  //   },
  // ];
  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | MultiAppsAuthenticationInfoRes>(null);

  const [rejectReason, setRejectReason] =
    useState<RejectProvisionReason | null>(null);
  useEffect(() => {
    if (
      exchangeInlineStatus === ExchangeInlineStatus.NotProvision ||
      exchangeInlineStatus === ExchangeInlineStatus.ProvisioningGrantStep1Done
    ) {
      ProvisionApi.apiGetMultiAppsAuthenticationInfos(
        Service.ExchangeInline,
        orgId
      )
        .then((res) => {
          setAuthenticationInfo(res.data);
        })
        .catch((e) => {
          const errorCode = e.data?.error?.details?.[0].errorCode;
          if (
            errorCode === RejectProvisionReason.ServiceConflict ||
            errorCode === RejectProvisionReason.PocConflict
          ) {
            setRejectReason(errorCode);
          }
        });
    }
  }, []);

  if (rejectReason) {
    return (
      <RejectProvisionModal
        onClose={onClose}
        service={SERVICE}
        reason={rejectReason}
      />
    );
  }

  //防止在加载数据的时候会闪烁
  if (
    (exchangeInlineStatus === ExchangeInlineStatus.NotProvision ||
      exchangeInlineStatus ===
        ExchangeInlineStatus.ProvisioningGrantStep1Done) &&
    !authenticationInfo
  ) {
    return <></>;
  }

  return (
    <>
      <Modal isOpen={isOpen} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          {/* TODO i18n "Grant Access to Exchange Inline" */}
          <ModalHeader>
            {t('provision.service.exchangeInline.title')}
          </ModalHeader>

          <ExchangeInlineLearnMore />

          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />

            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}
            <GrantPermissionSteps
              {...{
                exchangeInlineStatus,
                setExchangeInlineStatus,
                authenticationInfo,
                orgId,
                setErrorMsg,
                appId: provisionStatus?.authorization?.apps?.[0].appId || '',
              }}
            />
            {exchangeInlineStatus ===
              ExchangeInlineStatus.ProvisioningGrantStep2Done &&
              (syncDataFailFlag ? (
                <DataSyncFailed
                  service={SERVICE}
                  onRetry={async () => {
                    const res = await ProvisionApi.apiRetrySyncData(
                      SERVICE,
                      orgId
                    );
                    if (res.data?.status === 'FAILURE') {
                      if ([813, 814].includes(res.data?.error)) {
                        setErrorMsg(
                          <Trans
                            i18nKey={`provision:provision.service.exchangeInline.error.saveInfo.FAILURE;${res.data?.error}`}
                          >
                            placeholder
                            <Link
                              href={getCasOLHLink(
                                CAS_DOC_LINK.provision.exchangeInline
                                  .failTroubleshooting,
                                i18next.language
                              )}
                              target="_blank"
                            />
                            placeholder
                          </Trans>
                        );
                      } else {
                        setErrorMsg(
                          t([
                            `provision:provision.service.exchangeInline.error.saveInfo.FAILURE;${res.data?.error}`,
                            'common:common.apiException.internalError',
                          ])
                        );
                      }
                    } else {
                      setErrorMsg(undefined);
                      setSyncDataFailFlag(false);
                    }
                  }}
                />
              ) : (
                <DataSyncing
                  onRefresh={async () => {
                    const res = await ProvisionApi.apiGetProvisionStatus(
                      SERVICE,
                      orgId
                    );
                    if (res.data.status === ProvisionStatusEnum.Provisioned) {
                      setExchangeInlineStatus(ExchangeInlineStatus.Provisioned);
                    }
                  }}
                />
              ))}
            {exchangeInlineStatus === ExchangeInlineStatus.Provisioned && (
              <DataSynced />
            )}
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.button.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ExchangeInlineModal;

//eslint-disable-next-line @typescript-eslint/no-unused-vars
const GrantPermissionSteps = ({
  exchangeInlineStatus,
  setExchangeInlineStatus,
  authenticationInfo,
  orgId,
  setErrorMsg,
  appId,
}: {
  exchangeInlineStatus: ExchangeInlineStatus;
  setExchangeInlineStatus: (status: ExchangeInlineStatus) => void;
  authenticationInfo: MultiAppsAuthenticationInfoRes | null;
  orgId: string;
  setErrorMsg: (msg: string | undefined) => void;
  appId: string;
}) => {
  const { t } = useTranslation('provision');
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const [loading, toggleLoading] = useToggle(false);
  const timeRef = useRef<any>(null);

  const onFirstPermission = () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service: 'exchangeInline',
    });
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const exchangeInlineProvisionInfo = getProvisionCookie(
        'provision_status_azuread_mail_flow_inline_ex'
      );
      if (exchangeInlineProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        //{"code": "123"}
        const { error, code } = JSON.parse(exchangeInlineProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveProvisionInfo(
              App.ExchangeInlineMailFlow,
              code,
              EnabledPolicyTypeEnum.None,
              orgId
            );
            if (res.data?.status === 'failure') {
              // failure data = {
              //     "status": "failure",
              //     "reason": "FAILURE;451"  |  "PROVISION;CONFLICT"
              // }
              setErrorMsg(
                t([
                  `provision.service.exchangeInline.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              const statusRes = await ProvisionApi.apiGetProvisionStatus(
                SERVICE,
                orgId
              );
              const inlineMailFlowTokenStatus =
                statusRes.data?.authorization?.apps?.[0]?.status;
              if (inlineMailFlowTokenStatus === TokenStatus.Created) {
                setExchangeInlineStatus(
                  ExchangeInlineStatus.ProvisioningGrantStep1Done
                );
              } else {
                setErrorMsg(
                  t(
                    'provision.service.exchangeInline.error.saveInfo.FAILURE;450'
                  )
                );
              }
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.exchangeInline.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };
  const onSecondPermission = () => {
    const authUrl = authenticationInfo?.[1]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    setErrorMsg(undefined);
    reportPendo('Provision.GrantPermission.Click', {
      orgId,
      service: App.ExchangeInlineMailFlow,
    });
    toggleLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      const exchangeInlineProvisionInfo = getProvisionCookie(
        'provision_status_azuread_mail_inline_graph_api'
      );
      if (exchangeInlineProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(exchangeInlineProvisionInfo);
        if (error === undefined || error.length === 0) {
          try {
            const res = await ProvisionApi.apiSaveProvisionInfo(
              App.ExchangeInline,
              code,
              EnabledPolicyTypeEnum.None,
              orgId
            );
            if (res.data?.message === 'failure') {
              // failure data = { status: -1, message: 'failure', reason: 'FAILURE;451' };
              setErrorMsg(
                t([
                  `provision.service.exchangeInline.error.saveInfo.${res.data?.reason}`,
                  'common.apiException.internalError',
                ])
              );
            } else {
              setExchangeInlineStatus(
                ExchangeInlineStatus.ProvisioningGrantStep2Done
              );
            }
          } catch {
            setErrorMsg(t('common.apiException.internalError'));
          }
        } else {
          setErrorMsg(t('provision.service.exchangeInline.error.deniedGrant'));
        }
        toggleLoading(false);
      }
    }, 1000);
  };

  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  return (
    <>
      {loading && <CSpinner />}
      <Steps
        direction="vertical"
        current={exchangeInlineStatus - 1}
        items={[
          {
            title: (
              <Text color={colorStyle.color.primary}>
                {t(
                  'provision.service.exchangeInline.configureMailFlowPermission'
                )}
              </Text>
            ),
            description:
              exchangeInlineStatus <
              ExchangeInlineStatus.ProvisioningGrantStep1Done ? (
                <Button
                  variant="secondary"
                  borderRadius="2rem"
                  onClick={() => {
                    onFirstPermission();
                  }}
                >
                  {t('provision.button.grantPermission')}
                </Button>
              ) : (
                <Text color={colorStyle.color.tertiary}>
                  {t('provision.common.permissionGranted')}
                </Text>
              ),
            icon: (
              <StepIcon
                checked={
                  exchangeInlineStatus >=
                  ExchangeInlineStatus.ProvisioningGrantStep1Done
                }
              />
            ),
          },
          {
            title: (
              <Text color={colorStyle.color.primary}>
                <Trans
                  i18nKey="provision.service.exchangeInline.assignAzureADRoles"
                  ns="provision"
                >
                  placeholder
                  <Link
                    href={getCasOLHLink(
                      CAS_DOC_LINK.provision.exchangeInline.instruction,
                      i18next.language
                    )}
                    target="_blank"
                  >
                    placeholder
                  </Link>
                  placeholder
                </Trans>
              </Text>
            ),
            description: (
              <>
                {exchangeInlineStatus <
                ExchangeInlineStatus.ProvisioningConfirmDone ? (
                  <Button
                    variant="secondary"
                    borderRadius="2rem"
                    onClick={() => {
                      setErrorMsg(undefined);
                      //exchange_inline/check_exchange_admin_role
                      ProvisionApi.apiCheckExchangeAdminRole(orgId)
                        .then((res) => {
                          if (res.data) {
                            setExchangeInlineStatus(
                              ExchangeInlineStatus.ProvisioningConfirmDone
                            );
                          } else {
                            setErrorMsg(
                              t(
                                'provision.service.exchangeInline.error.saveInfo.FAILURE;817'
                              )
                            );
                          }
                        })
                        .catch(() => {
                          setErrorMsg(
                            t(
                              'provision.service.exchangeInline.error.saveInfo.FAILURE;810'
                            )
                          );
                        });
                    }}
                    disabled={
                      !(
                        exchangeInlineStatus ===
                        ExchangeInlineStatus.ProvisioningGrantStep1Done
                      )
                    }
                  >
                    {t('provision.button.confirmRolesAssigned')}
                  </Button>
                ) : (
                  <Text color={colorStyle.color.tertiary}>
                    {t('provision.service.exchangeInline.assignedDone')}
                  </Text>
                )}
                {exchangeInlineStatus >=
                  ExchangeInlineStatus.ProvisioningGrantStep1Done &&
                  exchangeInlineStatus <
                    ExchangeInlineStatus.ProvisioningGrantStep2Done && (
                    <Alert mt="8px" variant="outline" severity="info">
                      {t('provision.common.assignAppIdTip', {
                        appId,
                      })}
                    </Alert>
                  )}
              </>
            ),
            icon: <UserIcon />,
          },
          {
            title: (
              <Text color={colorStyle.color.primary}>
                {t('provision.service.exchangeInline.syncDataPermission')}
              </Text>
            ),
            description:
              exchangeInlineStatus <
              ExchangeInlineStatus.ProvisioningGrantStep2Done ? (
                <Button
                  variant="secondary"
                  borderRadius="2rem"
                  disabled={
                    !(
                      exchangeInlineStatus ===
                      ExchangeInlineStatus.ProvisioningConfirmDone
                    )
                  }
                  onClick={() => {
                    onSecondPermission();
                  }}
                >
                  {t('provision.button.grantPermission')}
                </Button>
              ) : (
                <Text color={colorStyle.color.tertiary}>
                  {t('provision.common.permissionGranted')}
                </Text>
              ),
            icon: (
              <StepIcon
                checked={
                  exchangeInlineStatus >=
                  ExchangeInlineStatus.ProvisioningGrantStep2Done
                }
              />
            ),
          },
        ]}
      />
    </>
  );
};
